// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * DSA 1.8 vector style maths (notes §7, §8): numbers and colours from
 * fields, easing curves, `when` conditions and the title/link templates.
 *
 * Everything here is pure and works on one feature's attribute record
 * (`attrs`: field name → cell), so the renderer and the tests share it.
 */
import type {
  LocalizedText, VectorColor, VectorCondition, VectorEasing, VectorField, VectorNumber, VectorPaletteStop, VectorScale,
} from './vectorTypes'
import { displayUnitOf } from './vectorUnits'

/** One feature's cells by field name. */
export type Attrs = Record<string, unknown>

/** Linear sRGB-space colour, components 0..1. */
export type Rgba = [number, number, number, number]

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * Piecewise map of `v` through matching domain/range entries (§7.1).
 * `t` is computed on the map's axis: sqrt or log of the value.
 */
function piecewise(v: number, domain: number[], range: number[], axis: (x: number) => number, clamp: boolean): number {
  const n = Math.min(domain.length, range.length)
  if (n === 0) return NaN
  if (n === 1) return range[0]
  const x = axis(v)
  let i = 0
  if (x <= axis(domain[0])) i = 0
  else if (x >= axis(domain[n - 1])) i = n - 2
  else while (i < n - 2 && x > axis(domain[i + 1])) i++
  const a = axis(domain[i])
  const b = axis(domain[i + 1])
  let t = b === a ? 0 : (x - a) / (b - a)
  if (clamp) t = Math.min(1, Math.max(0, t))
  return range[i] + t * (range[i + 1] - range[i])
}

const AXES: Record<string, (x: number) => number> = {
  linear: (x) => x,
  sqrt: (x) => Math.sqrt(Math.max(0, x)),
  log: (x) => Math.log(Math.max(Number.MIN_VALUE, x)),
}

/** Number of thresholds ≤ v (step maps). */
function stepIndex(v: number, thresholds: number[]): number {
  let i = 0
  while (i < thresholds.length && thresholds[i] <= v) i++
  return i
}

/** A scale's value for one feature, or null when the feature is not drawn. */
export function evalScale(scale: VectorScale, attrs: Attrs): number | null {
  const v = num(attrs[scale.field])
  if (v === null) return scale.nullValue ?? null
  const map = scale.map ?? 'linear'
  if (map === 'step') return scale.range[Math.min(stepIndex(v, scale.domain), scale.range.length - 1)]
  const out = piecewise(v, scale.domain, scale.range, AXES[map] ?? AXES.linear, scale.clamp !== false)
  return Number.isFinite(out) ? out : null
}

/** A constant or a scaled number (`vectorNumber`); `fallback` when absent. */
export function evalNumber(value: VectorNumber | undefined, attrs: Attrs, fallback: number): number | null {
  if (value === undefined) return fallback
  if (typeof value === 'number') return value
  return evalScale(value, attrs)
}

/** `#rrggbb` / `#rrggbbaa` → 0..1 components; null when malformed. */
export function parseHex(hex: string | undefined): Rgba | null {
  if (!hex) return null
  const m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(hex)
  if (!m) return null
  const n = parseInt(m[1], 16)
  const a = m[2] ? parseInt(m[2], 16) / 255 : 1
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255, a]
}

/** Palette stops at `t` (0..1), linear in sRGB between stops (§7.3). */
export function paletteAt(stops: VectorPaletteStop[], t: number): Rgba {
  const s = [...stops].sort((a, b) => a.t - b.t)
  const x = Math.min(1, Math.max(0, t))
  if (x <= s[0].t) return s[0].rgba.map((c) => c / 255) as Rgba
  for (let i = 0; i < s.length - 1; i++) {
    const a = s[i]
    const b = s[i + 1]
    if (x <= b.t) {
      const f = b.t === a.t ? 0 : (x - a.t) / (b.t - a.t)
      return a.rgba.map((c, k) => (c + f * (b.rgba[k] - c)) / 255) as Rgba
    }
  }
  return s[s.length - 1].rgba.map((c) => c / 255) as Rgba
}

/**
 * A feature's colour (§7.3), or null when it is not drawn (a null value
 * without `nullColor`, or a category without a colour).
 */
export function evalColor(color: VectorColor | undefined, attrs: Attrs, fields: VectorField[], fallback: Rgba): Rgba | null {
  if (!color) return fallback
  if ('constant' in color) return parseHex(color.constant)
  const raw = attrs[color.field]
  if (raw === null || raw === undefined) return parseHex(color.nullColor)
  if (color.map === 'category') {
    const field = fields.find((f) => f.name === color.field)
    const cat = field?.categories?.find((c) => c.value === raw)
    return parseHex(cat?.color) ?? parseHex(color.nullColor)
  }
  const v = num(raw)
  if (v === null) return parseHex(color.nullColor)
  if ('colors' in color) return parseHex(color.colors[Math.min(stepIndex(v, color.domain), color.colors.length - 1)])
  const [d0, d1] = color.domain
  const axis = color.map === 'log' ? AXES.log : AXES.linear
  const t = axis(d1) === axis(d0) ? 0 : (axis(v) - axis(d0)) / (axis(d1) - axis(d0))
  return paletteAt(color.palette.stops, t)
}

/** p(x) for the easings of §7.4. */
export function ease(easing: VectorEasing | string | undefined, x: number): number {
  const t = Math.min(1, Math.max(0, x))
  switch (easing) {
    case 'easeInQuad': return t * t
    case 'easeOutQuad': return 1 - (1 - t) * (1 - t)
    case 'easeInOutQuad': return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
    case 'easeInCubic': return t * t * t
    case 'easeOutCubic': return 1 - (1 - t) ** 3
    case 'easeInOutCubic': return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
    case 'easeOutBack': return 1 + 2.70158 * (t - 1) ** 3 + 1.70158 * (t - 1) ** 2
    default: return t
  }
}

/** §7.5: true when every comparison holds; a null value never matches. */
export function matches(cond: VectorCondition, attrs: Attrs): boolean {
  const v = attrs[cond.field]
  if (v === null || v === undefined) return false
  const n = num(v)
  if (cond.gte !== undefined && !(n !== null && n >= cond.gte)) return false
  if (cond.gt !== undefined && !(n !== null && n > cond.gt)) return false
  if (cond.lte !== undefined && !(n !== null && n <= cond.lte)) return false
  if (cond.lt !== undefined && !(n !== null && n < cond.lt)) return false
  if (cond.eq !== undefined && v !== cond.eq) return false
  if (cond.in !== undefined && !cond.in.includes(v as string | number)) return false
  return true
}

/** The viewer's language, then English, then whatever is there. */
export function localize(text: LocalizedText | undefined, lang: string): string {
  if (text === undefined) return ''
  if (typeof text === 'string') return text
  const base = lang.split('-')[0]
  return text[lang] ?? text[base] ?? text.en ?? Object.values(text)[0] ?? ''
}

const TIME_ROLES = new Set(['time', 'start', 'end'])

/**
 * One cell as the viewer reads it (§8): numbers with the field's
 * precision, times as UTC ISO 8601 from the epoch, categories by label,
 * speeds in km/h (vectorUnits.ts).
 */
export function formatCell(field: VectorField | undefined, value: unknown, lang: string, epochMs: number): string {
  if (value === null || value === undefined) return ''
  if (!field) return String(value)
  if (TIME_ROLES.has(field.role) && typeof value === 'number') {
    return new Date(epochMs + value * 1000).toISOString().replace('.000Z', 'Z')
  }
  if (field.type === 'category') {
    const cat = field.categories?.find((c) => c.value === value)
    return cat ? localize(cat.label, lang) : String(value)
  }
  const unit = displayUnitOf(field)
  if (typeof value === 'number' && unit) {
    return (value * unit.factor).toLocaleString(lang, { minimumFractionDigits: unit.precision, maximumFractionDigits: unit.precision })
  }
  if (typeof value === 'number' && field.precision !== undefined) {
    return value.toLocaleString(lang, { minimumFractionDigits: field.precision, maximumFractionDigits: field.precision })
  }
  if (Array.isArray(value)) return ''
  return String(value)
}

/** The units shown after a field's value: the display units, else the published ones. */
export function shownUnits(field: VectorField | undefined): string {
  return displayUnitOf(field)?.units ?? field?.units ?? ''
}

/**
 * `{name}` → formatted cell (title, label) or URL-encoded raw cell (link).
 * In text, a field shown in other units than published takes the units
 * written right after it along: "{wind} m/s" reads "250 km/h".
 */
export function fillTemplate(
  template: string, attrs: Attrs, fields: VectorField[], lang: string, epochMs: number, mode: 'text' | 'link',
): string {
  const re = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g
  let out = ''
  let from = 0
  for (let m = re.exec(template); m; m = re.exec(template)) {
    out += template.slice(from, m.index)
    from = re.lastIndex
    const name = m[1]
    const value = attrs[name]
    if (mode === 'link') { out += value === null || value === undefined ? '' : encodeURIComponent(String(value)); continue }
    const field = fields.find((f) => f.name === name)
    out += formatCell(field, value, lang, epochMs)
    const unit = displayUnitOf(field)
    if (unit && field?.units) {
      const after = /^(\s*)([^\s),.;]+)/.exec(template.slice(from))
      if (after && after[2].toLowerCase() === field.units.toLowerCase()) {
        out += `${after[1] || ' '}${unit.units}`
        from += after[0].length
        re.lastIndex = from
      }
    }
  }
  return out + template.slice(from)
}
